import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import MarkdownLite from "@/components/MarkdownLite";
import ShareBar from "@/components/ShareBar";
import { Loader2, ArrowLeft } from "lucide-react";

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then(({ data }) => {
        setPost(data);
        document.title = `${data.title} — OpenWord AI`;
        setMeta("description", data.meta_description || data.excerpt || "");
        setMeta("og:title", data.title, "property");
        setMeta("og:description", data.meta_description || data.excerpt || "", "property");
        setMeta("og:image", `${process.env.REACT_APP_BACKEND_URL}/api/blog/${slug}/${data.has_photo ? "post-image.png?shape=landscape" : "image.png"}`, "property");
      })
      .catch(() => {
        // Post was removed / never existed — never show a dead page. Send them to the funnel.
        setPost(null);
        navigate(`/welcome?utm_source=blog&utm_medium=redirect&utm_campaign=${slug}`, { replace: true });
      });
  }, [slug, navigate]);

  return (
    <div className="min-h-screen bg-[#07090E]">
      <header className="border-b border-[#1E273D] bg-[#0E131F]/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/blog" data-testid="blog-back" className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#00E5FF]">
            <ArrowLeft className="w-4 h-4" /> All posts
          </Link>
          <Link to={`/login?utm_source=blog&utm_medium=blog_cta&utm_campaign=${slug}`} data-testid="post-cta-header" className="text-sm font-medium text-[#00E5FF] hover:text-[#00B8CC]">Read along</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-14">
        {post === undefined && (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        )}
        {post === null && (
          <div className="text-center py-20">
            <p className="text-2xl text-slate-200">Post not found</p>
            <Link to="/blog" className="text-[#00E5FF] text-sm mt-3 inline-block">Back to the blog</Link>
          </div>
        )}
        {post && (
          <article data-testid="blog-post">
            <div className="text-xs uppercase tracking-widest text-slate-500">{fmtDate(post.published_at)}</div>
            <h1 className="text-4xl sm:text-5xl text-white leading-tight mt-2">{post.title}</h1>
            <div className="mt-4">
              <ShareBar url={`${window.location.origin}/blog/${slug}`} hook={post.title} campaign={slug} testid="post-share-top" />
            </div>
            {post.has_photo && (
              <div className="mt-8 aspect-[16/9] w-full overflow-hidden rounded-2xl bg-[#141B29]">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/blog/${slug}/post-image.png?shape=landscape`}
                  alt={post.image_alt || post.title}
                  data-testid="blog-post-hero"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="mt-8 prose-openword text-lg leading-[1.9] text-slate-200">
              <MarkdownLite text={post.body_markdown} />
            </div>

            <div className="mt-10 pt-6 border-t border-[#1E273D]">
              <ShareBar url={`${window.location.origin}/blog/${slug}`} hook={post.title} campaign={slug} testid="post-share-bottom" />
            </div>

            <div className="mt-8 rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 text-center">
              <p className="text-xl text-slate-200">Sit with this Scripture yourself.</p>
              <p className="text-sm text-slate-400 mt-1">OpenWord AI is a Scripture-first study tool — free to try.</p>
              <Link
                to={`/login?utm_source=blog&utm_medium=blog_cta&utm_campaign=${slug}`}
                data-testid="post-cta-footer"
                className="inline-flex items-center gap-2 mt-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-6 py-3 text-sm font-medium transition-colors"
              >
                Sit with it yourself
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
